import React, { FC, useState } from 'react';
import {
	DialogProps,
	Box,
	Tooltip,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Popper,
	ClickAwayListener,
} from '@mui/material';
import { loaderWrapperStyles } from '../../../_core/components/_ui/core-modal/core-modal.styles';
import { useTranslation } from 'react-i18next';
import { CoreIconButton } from '../../../_core/components/_ui/core-button';
import { Close } from '../../../_core/constants/icons.constants';
import { CoreLoadingWall } from '../../../_core/components/_ui/core-loading-wall';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { CoreTableEmptyBody } from '../../../_core/components/_ui/core-table';

export interface CommunicationLogsModalProps extends DialogProps {
	data: any;
	open: boolean;
	handleClickAway(): void;
	anchorRef: any;
	loading?: boolean;
}

export const CommunicationLogsModal: FC<CommunicationLogsModalProps> = (props) => {
	const { data, open, handleClickAway, anchorRef, loading } = props;

	const [t] = useTranslation();
	const [expandedStates, setExpandedStates] = useState<any>({});

	const handleChange = (index: number) => (event: any, isExpanded: boolean) => {
		setExpandedStates((prev: any) => ({
			...prev,
			[index]: isExpanded,
		}));
	};

	return (
		<Popper
			open={open}
			anchorEl={anchorRef.current}
			placement="bottom-end"
			aria-labelledby={'Communication log'}
			sx={{ width: '500px', zIndex: '10', borderRadius: '12px', border: '1px solid #DCDFE7' }}
		>
			<ClickAwayListener onClickAway={handleClickAway}>
				<Box
					sx={{
						pt: 2,
						bgcolor: (theme) => theme.colors.white,
						borderRadius: 1,
						display: 'flex',
						flexDirection: 'column',
						maxHeight: '100%',
						overflow: 'hidden',
						width: '100%',
					}}
				>
					<Tooltip title={t('buttons.tooltips.close')}>
						<CoreIconButton
							sx={{ color: (theme) => theme.colors.black, position: 'absolute', right: 12, top: 12, zIndex: '20' }}
							onClick={handleClickAway}
						>
							<Close />
						</CoreIconButton>
					</Tooltip>

					<Box sx={loaderWrapperStyles}>
						<Box sx={{ overflow: 'auto', px: 2, pt: 0, pb: 2, flex: 1 }}>
							<Typography
								variant="h5"
								sx={{
									fontSize: '16px',
									fontWeight: 500,
									textTransform: 'capitalize',
									mb: 3,
								}}
							>
								{'Communication Log'}
							</Typography>

							<Box>
								<Box>
									{!data?.length || !data.some((el: any)=> el.communicationRecords?.length)? (
										<CoreTableEmptyBody description={'Threre are no records in history yet'} />
									) : (
										data?.map((el: any, index: number) => {
											const isExpanded = !!expandedStates[index];
											const { family, communicationRecords } = el;

											[...communicationRecords].sort(
												(a: any, b: any) => new Date(b?.date).getTime() - new Date(a?.date).getTime()
											);
											return communicationRecords?.length ? (
												<Accordion key={index} expanded={isExpanded} onChange={handleChange(index)}>
													<AccordionSummary
														expandIcon={<ExpandMoreIcon />}
														aria-controls="panel2-content"
														id="panel2-header"
														sx={{
															display: 'flex',
															alignItems: 'flex-start',
															p: '12px',
															position: 'relative',
															'& .MuiAccordionSummary-expandIconWrapper': {
																position: 'absolute',
																right: 8,
																top: 12,
																// 	transform: 'translateY(-50%)',
															},
															'& .MuiAccordionSummary-content': { m: 0 },
															'&.Mui-expanded': {
																minHeight: 0,
																'& .MuiAccordionSummary-content': { m: 0 },
															},
															'& .MuiButtonBase-root.MuiAccordionSummary-root': {
																p: 0,
															},
														}}
													>
														<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
															<Typography
																sx={{
																	display: 'flex',
																	gap: '6px',
																	alignItems: 'center',
																	fontSize: '14px',
																	fontWeight: 600,
																}}
															>
																{family.fullName}{' '}
																<Typography
																	component="span"
																	sx={{ fontSize: '12px', color: (theme) => theme.colors.grayBlue, fontWeight: 400 }}
																>{` (${communicationRecords?.length} ${
																	communicationRecords?.length > 1 ? 'actions' : 'action'
																})`}</Typography>
															</Typography>
															{!isExpanded && (
																<Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mt: 2 }}>
																	<Typography
																		sx={{ fontSize: '12px', fontWeight: 400 }}
																	>{`Last action: ${communicationRecords[0]?.message}`}</Typography>
																	<Typography sx={{ width: '140px', fontSize: '12px', fontWeight: 400 }}>
																		{communicationRecords[0]?.date
																			? format(new Date(communicationRecords[0]?.date), "MM.dd.yy 'at' hh:mm aaa")
																			: null}
																	</Typography>
																</Box>
															)}
														</Box>
													</AccordionSummary>
													<AccordionDetails
														sx={{
															display: 'flex',
															flexDirection: 'column',
															gap: '12px',
															px: '12px',
															pb: '12px',
															pt: 0,
														}}
													>
														{communicationRecords?.map((record: any) => (
															<Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
																<Typography sx={{ fontSize: '12px', fontWeight: 400 }}>{record?.message}</Typography>
																<Typography sx={{ width: '140px', fontSize: '12px', fontWeight: 400 }}>
																	{format(new Date(record?.date), "MM.dd.yy 'at' hh:mm aaa")}
																</Typography>
															</Box>
														))}
													</AccordionDetails>
												</Accordion>
											) : null;
										})
									)}
								</Box>
							</Box>
						</Box>
						{loading && <CoreLoadingWall />}
					</Box>
				</Box>
			</ClickAwayListener>
		</Popper>
	);
};
